import { Color, ColorFill, Container, SvgGraphics } from '@tencent/alloy-renderer';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { isMetaOrShiftOrCtrlKey } from '@src/utils/key-mouse-event/mouse-event-check';
import { selectCell, selectColAtBottom, selectColAtTop, selectRowAtLeft, selectRowAtRight } from './cursor';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';

export class CellSelectComponent extends Container {
    private cachedSelectors: Map<string, SvgGraphics>;

    private topColSelector: SvgGraphics | null;
    private bottomColSelector: SvgGraphics | null;
    private leftRowSelector: SvgGraphics | null;
    private rightRowSelector: SvgGraphics | null;

    public constructor(private factoryOptions: InterfaceFactoryOptions<ITableModel>) {
        super();
        this.cachedSelectors = new Map();
    }

    public updateComponent(): void {
        this.addRowColSelector();

        const { model } = this.factoryOptions;
        const scale = this.factoryOptions.getPageScale();
        const width = 8.0 / scale;
        const resueKeys = Array.from(this.cachedSelectors.keys());
        model.tableItems.forEach((rowInfo, row) => {
            rowInfo.forEach((cell, col) => {
                if (!cell.hMerge && !cell.vMerge) {
                    const { left, top, height } = cell.props.style;
                    let selector = this.cachedSelectors.get(cell.key);
                    if (!selector) {
                        selector = new SvgGraphics();
                        selector.fill = new ColorFill(Color.Transparent);
                        selector.cursor = selectCell;
                        this.addChild(selector);
                        selector.onMouseDown(this.handleSelectCell);
                        this.cachedSelectors.set(cell.key, selector);
                    } else {
                        resueKeys.splice(resueKeys.indexOf(cell.key), 1);
                    }
                    selector.x = left;
                    selector.y = top;
                    selector.path = `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;
                    Object.assign(selector, { row, col });
                }
            });
        });

        resueKeys.forEach((key) => {
            const selector = this.cachedSelectors.get(key);
            if (selector) {
                this.removeChild(selector);
                this.cachedSelectors.delete(key);
            }
        });
    }

    private addRowColSelector() {
        const scale = this.factoryOptions.getPageScale();
        const { width, height } = this.factoryOptions.model.props.style;
        const colSelectorPath = `M 0 0 L ${width} 0 L ${width} ${10 / scale} L 0 ${10 / scale} Z`;
        const rowSelectorPath = `M 0 0 L ${10 / scale} 0 L ${10 / scale} ${height} L 0 ${height} Z`;
        const hitColor = new ColorFill(Color.Transparent);

        if (!this.topColSelector) {
            this.topColSelector = new SvgGraphics();
            this.topColSelector.fill = hitColor;
            this.topColSelector.cursor = selectColAtTop;
            this.topColSelector.onMouseDown(this.handleColSelectorMouseDown);
            this.addChild(this.topColSelector);
        }
        this.topColSelector.path = colSelectorPath;
        this.topColSelector.x = 0;
        this.topColSelector.y = -10 / scale;

        if (!this.bottomColSelector) {
            this.bottomColSelector = new SvgGraphics();
            this.bottomColSelector.fill = hitColor;
            this.bottomColSelector.cursor = selectColAtBottom;
            this.bottomColSelector.onMouseDown(this.handleColSelectorMouseDown);
            this.addChild(this.bottomColSelector);
        }
        this.bottomColSelector.path = colSelectorPath;
        this.bottomColSelector.x = 0;
        this.bottomColSelector.y = height;

        if (!this.leftRowSelector) {
            this.leftRowSelector = new SvgGraphics();
            this.leftRowSelector.fill = hitColor;
            this.leftRowSelector.cursor = selectRowAtLeft;
            this.leftRowSelector.onMouseDown(this.handleRowSelectorMouseDown);
            this.addChild(this.leftRowSelector);
        }
        this.leftRowSelector.path = rowSelectorPath;
        this.leftRowSelector.x = -10 / scale;
        this.leftRowSelector.y = 0;

        if (!this.rightRowSelector) {
            this.rightRowSelector = new SvgGraphics();
            this.rightRowSelector.fill = hitColor;
            this.rightRowSelector.cursor = selectRowAtRight;
            this.rightRowSelector.onMouseDown(this.handleRowSelectorMouseDown);
            this.addChild(this.rightRowSelector);
        }
        this.rightRowSelector.path = rowSelectorPath;
        this.rightRowSelector.x = width;
        this.rightRowSelector.y = 0;
    }

    private handleSelectCell = (e): void => {
        e.stopPropagation();
        e.preventDefault();
        if (!isMetaOrShiftOrCtrlKey(e.nativeEvent)) {
            const { logicService } = this.factoryOptions;
            const tableEditLogic = logicService.getLogic(this.factoryOptions) as any;
            tableEditLogic.makeElementSelected(e.nativeEvent as MouseEvent);
            const { row, col } = e.target;
            this.factoryOptions.model.showSelection = true;
            this.factoryOptions.model.afterSelection(row, col, row, col);
        }
    };

    private handleColSelectorMouseDown = (e): void => {
        this.handleSelectTable(e);
        if (e.target === this.topColSelector || e.target === this.bottomColSelector) {
            const scale = this.factoryOptions.getPageScale();
            const { logicService, model } = this.factoryOptions;
            const tableEditLogic = logicService.getLogic(this.factoryOptions) as any;
            const col = tableEditLogic.getIndex(model.props.colWidths, (e.worldX - this.worldBounds.x) / scale);
            if (!isMetaOrShiftOrCtrlKey(e)) {
                model.showSelection = true;
                model.afterSelection(0, col, model.props.rowHeights.length - 1, col);
            }
        }
    };

    private handleRowSelectorMouseDown = (e): void => {
        this.handleSelectTable(e);
        if (e.target === this.leftRowSelector || e.target === this.rightRowSelector) {
            const scale = this.factoryOptions.getPageScale();
            const { logicService, model } = this.factoryOptions;
            const tableEditLogic = logicService.getLogic(this.factoryOptions) as any;
            const row = tableEditLogic.getIndex(model.displayRowHeights, (e.worldY - this.worldBounds.y) / scale);
            if (!isMetaOrShiftOrCtrlKey(e)) {
                model.showSelection = true;
                model.afterSelection(row, 0, row, model.props.colWidths.length - 1);
            }
        }
    };

    private handleSelectTable = (e): void => {
        e.stopPropagation();
        e.preventDefault();
        if (!isMetaOrShiftOrCtrlKey(e.nativeEvent)) {
            const { logicService } = this.factoryOptions;
            const tableEditLogic = logicService.getLogic(this.factoryOptions) as any;
            tableEditLogic.makeElementSelected(e.nativeEvent as MouseEvent);
        }
    };
}
